import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import { 
  GraduationCap, 
  ArrowLeft,
  Calendar,
  Clock,
  Video,
  AlertTriangle,
  XCircle
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const SessionPolicyPage = () => {
  return (
    <div className="min-h-screen bg-stone-50" data-testid="session-policy-page">
      {/* Header */}
      <header className="bg-white border-b border-stone-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <Link to="/" className="flex items-center gap-2">
                <GraduationCap className="h-8 w-8 text-indigo-700" />
                <span className="font-heading text-xl sm:text-2xl font-semibold text-slate-900">
                  Gifted Academy Tutors
                </span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-12 md:py-16 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center">
              <Calendar className="h-8 w-8 text-blue-700" />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-medium text-slate-900">
                Session Policy
              </h1>
              <p className="text-slate-500">Cancellation, No-Show & Recording Policies</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="prose prose-slate max-w-none space-y-8">
            
            {/* Cancellation Policy */}
            <div className="bg-white rounded-2xl border border-stone-200 p-8 md:p-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-amber-700" />
                </div>
                <h2 className="font-heading text-2xl font-semibold text-slate-900">
                  Session Cancellation Policy
                </h2>
              </div>

              <div className="space-y-4">
                <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
                  <p className="text-slate-700 leading-relaxed font-medium">
                    Students or parents must cancel or reschedule a tutoring session at least <strong>6 hours before</strong> the scheduled session time.
                  </p>
                </div>

                <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 mt-1 flex-shrink-0" />
                    <p className="text-slate-700 leading-relaxed">
                      If cancellation occurs <strong>less than 6 hours</strong> before the session, the tutoring session will be considered missed and the <strong>tutoring hour will be deducted</strong> from the student's package.
                    </p>
                  </div>
                </div>

                <p className="text-slate-600 leading-relaxed">
                  To cancel or reschedule a session, please contact us via:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Live chat on our website (available 24/7)</li>
                  <li>Email: contact@giftedacademytutors.ca</li>
                  <li>Phone: 416-846-5062 (Monday-Sunday, 9am-9pm)</li>
                </ul>
              </div>
            </div>

            {/* No-Show Policy */}
            <div className="bg-white rounded-2xl border border-stone-200 p-8 md:p-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                  <XCircle className="h-5 w-5 text-red-700" />
                </div>
                <h2 className="font-heading text-2xl font-semibold text-slate-900">
                  No-Show Policy
                </h2>
              </div>

              <div className="space-y-4">
                <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                  <p className="text-slate-700 leading-relaxed">
                    Tutors will join the session at the scheduled time and <strong>wait up to 30 minutes</strong> for the student.
                  </p>
                </div>

                <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 mt-1 flex-shrink-0" />
                    <p className="text-slate-700 leading-relaxed">
                      If the student does not join within 30 minutes, the session will be marked as a <strong>No-Show</strong> and the <strong>tutoring hour will be deducted</strong> from the student's package.
                    </p>
                  </div>
                </div>

                <p className="text-slate-600 leading-relaxed">
                  To avoid no-shows:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Set reminders for your scheduled sessions</li>
                  <li>Test your internet connection and equipment before the session</li>
                  <li>Have the session link ready 5 minutes before the scheduled time</li>
                  <li>Contact us immediately if you experience technical difficulties</li>
                </ul>
              </div>
            </div>

            {/* Recording Policy */}
            <div className="bg-white rounded-2xl border border-stone-200 p-8 md:p-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Video className="h-5 w-5 text-purple-700" />
                </div>
                <h2 className="font-heading text-2xl font-semibold text-slate-900">
                  Recording Policy
                </h2>
              </div>

              <div className="space-y-4">
                <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                  <p className="text-slate-700 leading-relaxed font-medium">
                    Tutoring sessions <strong>may not be recorded</strong> by students or tutors.
                  </p>
                </div>

                <p className="text-slate-600 leading-relaxed">
                  This includes:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Video recording</li>
                  <li>Audio recording</li>
                  <li>Screen recording</li>
                  <li>Screenshots of the session</li>
                  <li>Any other method of capturing the tutoring session</li>
                </ul>

                <div className="bg-purple-50 rounded-xl p-6 border border-purple-200 mt-4">
                  <p className="text-slate-700 leading-relaxed">
                    Recording of sessions is <strong>strictly prohibited</strong> unless explicit written permission is granted by Gifted Academy Tutors. Violation of this policy may result in account suspension.
                  </p>
                </div>

                <p className="text-slate-600 leading-relaxed mt-4">
                  If you need a recording for accessibility or educational purposes, please contact us to discuss alternatives such as:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Detailed lesson notes after each session</li>
                  <li>Written summaries of key concepts covered</li>
                  <li>Additional practice materials</li>
                </ul>
              </div>
            </div>

            {/* Session Best Practices */}
            <div className="bg-white rounded-2xl border border-stone-200 p-8 md:p-12">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-6">
                Session Best Practices
              </h2>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-2">Before the Session</h4>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>• Prepare your questions in advance</li>
                    <li>• Test your audio and video</li>
                    <li>• Find a quiet, well-lit space</li>
                    <li>• Have necessary materials ready</li>
                  </ul>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-2">During the Session</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Engage actively with the tutor</li>
                    <li>• Ask questions when unclear</li>
                    <li>• Take notes as needed</li>
                    <li>• Minimize distractions</li>
                  </ul>
                </div>
              </div>
            </div>

          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SessionPolicyPage;
